import { createContext } from 'react'

import { noop } from 'lodash-es'

import { MIXPANEL_PROPERTY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { ExperimentDataParams } from '@lyrahealth-inc/shared-app-logic/src/features/experiments/hooks/useSendExperimentData'
import { AiInTriageThreeArmVariation } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'

export const AppContext = createContext<{
  customerName?: string
  detectedCountryIso?: string
  domain?: string
  copyPasteDisabled?: boolean
  setModalStatusOpen?: (status: boolean) => void
  isPsaBannerDismissed?: boolean
  /* BEGIN feature flags */
  isLatestMatchesEnabled?: boolean
  isCHCOutage?: boolean
  isHPIConsentWithMedicareEnabled?: boolean
  shouldShowLCPositioning?: boolean
  hideUserTestimonialsFromLCxModal?: boolean
  isDedicatedCareNavigatorEnabled?: boolean
  shouldSeePendoGuides?: boolean
  shouldShowCostEstimatesAsFullyCovered?: boolean
  isCostMessagingEnabled?: boolean
  isCustomerDynamicSessionLimitEnabled?: boolean
  isFamilyHubEnabled?: boolean
  isPreferredNameEnabled?: boolean
  shouldShowNewMinimizedIntegratedBenefitsDesign?: boolean
  progressBarPercentage?: number
  showPickedForYouV2?: boolean
  showPickedForYouV3?: boolean
  routeUserDirectlyToAgentInInAppMessagingChatWindow?: boolean
  chatSessionTimeoutDuration?: number
  shouldShowAvailabilityPreference?: boolean
  isPhoneNumberRequiredForApptRequest?: boolean
  forceAdultFlowsToHPI?: boolean
  isInProductCalendarAddModalEnabled?: boolean
  emailCapturePageExperimentV2?: string
  shouldOnlySeeCareNavigatorContactScreenInPlatform?: boolean
  parentTeenBookingImprovementsEnabled?: boolean
  canSelectMultiplePrimaryNeeds?: boolean
  shouldShowConfirmationPageCapacityChecks?: boolean
  isExpandableWorkingHoursEnabled?: boolean
  isSelectWellnessToolkitsRound2Enabled?: boolean
  areGlobalPreferencesEnabled?: boolean
  showAiCoach?: boolean
  isAiCoachVoiceEnabled?: boolean
  isMemberSwitchEmployerSelfServeEnabled?: boolean
  shouldShowMessengerDraftsUtility?: boolean
  isHideCostAndSessionLimitEnabled?: boolean
  shouldHideEligibilityTypeQuestion?: boolean
  isAiInTriageEnabledThreeArm?: string
  isNewBenefitsPageDesignEnabled?: boolean
  showParentLedLCTTeenBookingUpdates?: boolean
  showMemberRecommendedBadge?: boolean
  isInProductCalendarModalityEnabledS4C?: boolean
  isClientDisabled?: boolean
  shouldDisplayHighRiskModalForDirectBooking?: boolean
  isBenefitsExtensionBannerDisplayed?: boolean
  areExpandedGlobalPreferencesEnabled?: boolean
  isToolkitDirectoryPageEnabled?: boolean
  isPromotionalSMSEnabled?: boolean
  isProviderLevelFeedbackEnabled?: boolean
  showProviderAdjectives?: boolean
  isNotificationCenterRefreshEnabled?: boolean
  isOnsiteEntrypointsEnabled?: boolean
  shouldPopulateApptPhoneNumber?: boolean
  isNeuroPilotEnabled?: boolean
  isDisasterResponseServiceEnabled?: boolean
  isLCTTeensAdditionalRemindersEnabled?: boolean
  isAmazonCostTransparencyEnabled?: boolean
  isAuthenticatorMFAEnabled?: boolean
  isBlendedCareMedsAvailable?: boolean
  isGooglePlacesAPIEnabled?: boolean
  is1Lyra?: boolean
  showCostSharingAccountPaymentMethod?: boolean
  /* END feature flags */

  /* Please be aware that using trackEvent may lead to loss of reusability within components
   that use this function. See "Tracking user interaction events with Mixpanel" in README
   for more details. */
  trackEvent?: (event: string, eventProps?: { [key in MIXPANEL_PROPERTY]?: any }) => void
  maxBenefitsToShowInExplorer?: number
  isClientDataShareEnabled?: boolean
  openBranchUrl?: (url: string) => void
  showCostTransparency?: boolean
  sendExperimentData?: ({ experimentUUID, joinKeys }: ExperimentDataParams) => void
  showCalendarScheduleSession?: boolean
  showLegacyScheduleSession?: boolean
  isSmartSearchEnabled?: boolean
  onNavbarLogoDefaultPress?: () => void
}>({
  customerName: '',
  detectedCountryIso: undefined,
  domain: '',
  copyPasteDisabled: false,
  setModalStatusOpen: noop,
  isLatestMatchesEnabled: false,
  trackEvent: noop,
  maxBenefitsToShowInExplorer: 3,
  isClientDataShareEnabled: false,
  isHPIConsentWithMedicareEnabled: false,
  shouldShowLCPositioning: false,
  hideUserTestimonialsFromLCxModal: false,
  isDedicatedCareNavigatorEnabled: false,
  shouldSeePendoGuides: false,
  shouldShowCostEstimatesAsFullyCovered: false,
  isCostMessagingEnabled: false,
  isCustomerDynamicSessionLimitEnabled: false,
  isFamilyHubEnabled: false,
  isPreferredNameEnabled: false,
  shouldShowNewMinimizedIntegratedBenefitsDesign: false,
  showPickedForYouV2: false,
  showPickedForYouV3: false,
  routeUserDirectlyToAgentInInAppMessagingChatWindow: false,
  chatSessionTimeoutDuration: 600,
  shouldShowAvailabilityPreference: false,
  shouldPopulateApptPhoneNumber: false,
  forceAdultFlowsToHPI: false,
  showCostTransparency: false,
  isInProductCalendarAddModalEnabled: false,
  emailCapturePageExperimentV2: 'Control',
  shouldOnlySeeCareNavigatorContactScreenInPlatform: false,
  sendExperimentData: noop,
  parentTeenBookingImprovementsEnabled: false,
  shouldShowConfirmationPageCapacityChecks: false,
  isExpandableWorkingHoursEnabled: false,
  isSelectWellnessToolkitsRound2Enabled: false,
  areGlobalPreferencesEnabled: false,
  showAiCoach: false,
  isAiCoachVoiceEnabled: false,
  isMemberSwitchEmployerSelfServeEnabled: false,
  shouldShowMessengerDraftsUtility: false,
  isHideCostAndSessionLimitEnabled: false,
  shouldHideEligibilityTypeQuestion: false,
  isAiInTriageEnabledThreeArm: AiInTriageThreeArmVariation.CONTROL,
  isNewBenefitsPageDesignEnabled: false,
  showParentLedLCTTeenBookingUpdates: false,
  showMemberRecommendedBadge: false,
  isInProductCalendarModalityEnabledS4C: false,
  isClientDisabled: false,
  shouldDisplayHighRiskModalForDirectBooking: false,
  areExpandedGlobalPreferencesEnabled: false,
  isToolkitDirectoryPageEnabled: false,
  isPromotionalSMSEnabled: false,
  isProviderLevelFeedbackEnabled: false,
  showProviderAdjectives: false,
  isNotificationCenterRefreshEnabled: false,
  isOnsiteEntrypointsEnabled: false,
  isNeuroPilotEnabled: false,
  isDisasterResponseServiceEnabled: false,
  isLCTTeensAdditionalRemindersEnabled: false,
  isSmartSearchEnabled: false,
  isAmazonCostTransparencyEnabled: false,
  isGooglePlacesAPIEnabled: false,
  showCostSharingAccountPaymentMethod: false,
  onNavbarLogoDefaultPress: () => {},
})
